import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ToasterService {
  private counter = 0;
  private duration = 5000;
  private readonly toastsSubject = new BehaviorSubject<ToastMessage[]>([]);
  readonly toasts$ = this.toastsSubject.asObservable();

  success(message: string, title?: string, icon?: string, duration?: number) {
    this.show('success', message, title, icon, duration ?? this.duration);
  }

  error(message: string, title?: string, icon?: string, duration?: number) {
    this.show('error', message, title, icon, duration ?? this.duration);
  }

  info(message: string, title?: string, icon?: string, duration?: number) {
    this.show('info', message, title, icon, duration ?? this.duration);
  }

  // Backwards-compatible: can be called with a string (like info) OR with a payload for a custom welcome toast.
  welcome(messageOrPayload: WelcomeToastPayload, title?: string, message?: string, iconOrDuration?: string | number, duration?: number) {
    if (typeof messageOrPayload === 'string') {
      this.show('info', messageOrPayload, title, typeof iconOrDuration === 'string' ? iconOrDuration : undefined, (duration ?? this.duration) as any);
      return;
    }

    const payload = messageOrPayload;
    const toastTitle = title || 'Bienvenue';
    const toastDuration = typeof iconOrDuration === 'number' ? iconOrDuration : this.duration;
    this.show('info', message, toastTitle, undefined, toastDuration, { variant: 'welcome', payload });
  }

  private show(
    type: ToastType,
    message: string,
    title?: string,
    icon?: string,
    duration?: number,
    extra?: Partial<ToastMessage>,
  ) {
    const id = ++this.counter;
    const toast: ToastMessage = { id, type, message, title, icon, ...(extra || {}) };
    const list = this.toastsSubject.value;
    this.toastsSubject.next([...list, toast]);
    setTimeout(() => this.dismiss(id), duration);
  }

  dismiss(id: number) {
    const list = this.toastsSubject.value.filter((t) => t.id !== id);
    this.toastsSubject.next(list);
  }
}

export type ToastType = 'success' | 'error' | 'info';
export interface ToastMessage {
  id: number;
  type: ToastType;
  message: string;
  title?: string;
  icon?: string;
  duration?: number;

  // Optional variants for custom layouts
  variant?: 'welcome';
  payload?: WelcomeToastPayload;
}

export interface WelcomeToastPayload {
  qualitativeAmount?: string | number;
  qualitativeIconUrl?: string | null;
  quantitativeAmount?: string | number;
  quantitativeIconUrl?: string | null;
}
